// src/components/registration/steps/userGroupSteps/UserGroupsWizard.jsx

import React, { useState } from "react";
import PropTypes from "prop-types";
import heroLogo from "../../../../../../images/heroLogo.png";
import StepBar from "../../../../../utils/StepBar";
import "../UserSystemCard.css"; // Reuse existing CSS for consistent styling

// Import step components
import UserGroupsStep1Intro from "./UserGroupsStep1Intro";
import UserGroupsStep2Configure from "./UserGroupsStep2Configure";
import UserGroupsStep3Confirm from "./UserGroupsStep3Confirm";

const UserGroupsWizard = ({
  onContinue,
  userEmail,
  company,
  collectionBrand,
}) => {
  const [cardStep, setCardStep] = useState(1);
  const [userGroups, setUserGroups] = useState([]);

  // Undo/Redo state
  const [history, setHistory] = useState([]);
  const [future, setFuture] = useState([]);

  /**
   * checkDuplicateEmails:
   * Scans the current userGroups to see if any of the provided emails are already in use
   * by any role in any group.
   */
  const checkDuplicateEmails = (emails) => {
    // Gather all emails from current userGroups
    const allEmails = userGroups.flatMap((g) =>
      g.roles.flatMap((r) => r.users)
    );

    for (const em of emails) {
      if (allEmails.includes(em)) {
        return false; // Found a duplicate
      }
    }
    return true;
  };

  // ------------------ CRUD Methods for userGroups ------------------ //
  const addUserGroup = () => {
    setHistory([...history, userGroups]);

    const newGroup = {
      groupName: "",
      hierarchy: userGroups.length + 1,
      roles: [],
    };
    setUserGroups([...userGroups, newGroup]);
    setFuture([]);
  };

  const removeUserGroup = (index) => {
    setHistory([...history, userGroups]);

    const updated = [...userGroups];
    updated.splice(index, 1);

    // Reassign hierarchy
    updated.forEach((group, idx) => {
      group.hierarchy = idx + 1;
    });

    setUserGroups(updated);
    setFuture([]);
  };

  const updateUserGroup = (index, field, value) => {
    setHistory([...history, userGroups]);

    const updated = [...userGroups];
    updated[index][field] = value;
    setUserGroups(updated);
    setFuture([]);
  };

  const addRole = (groupIndex) => {
    setHistory([...history, userGroups]);

    const updated = [...userGroups];
    updated[groupIndex].roles.push({
      roleName: "",
      users: [],
    });
    setUserGroups(updated);
    setFuture([]);
  };

  const removeRole = (groupIndex, roleIndex) => {
    setHistory([...history, userGroups]);

    const updated = [...userGroups];
    updated[groupIndex].roles.splice(roleIndex, 1);
    setUserGroups(updated);

    setFuture([]);
  };

  const updateRole = (groupIndex, roleIndex, field, value) => {
    setHistory([...history, userGroups]);

    const updated = [...userGroups];
    updated[groupIndex].roles[roleIndex][field] = value;
    setUserGroups(updated);
    setFuture([]);
  };

  // ------------------ Undo / Redo Methods ------------------ //
  const undo = () => {
    if (history.length === 0) return;
    const prev = history[history.length - 1];
    const newHist = history.slice(0, history.length - 1);
    setFuture([userGroups, ...future]);
    setUserGroups(prev);
    setHistory(newHist);
  };

  const redo = () => {
    if (future.length === 0) return;
    const next = future[0];
    const newFut = future.slice(1);
    setHistory([...history, userGroups]);
    setUserGroups(next);
    setFuture(newFut);
  };

  return (
    <div className="step-add-user-groups-container">
      <div className="top-bar">
        <div className="logo-container">
          <img src={heroLogo} alt="Hero Logo" className="logo-small" />
        </div>
        <StepBar totalSteps={4} currentStep={3} />
      </div>
      <h2 className="step-title centered">Configure User Groups</h2>
      <p className="step-description centered">
        Set up your organization's structure. Create and manage User Groups,
        define Roles within each group, and assign Users to Roles.
      </p>
      <div className="user-system-card system-card">
        {cardStep === 1 && <UserGroupsStep1Intro setCardStep={setCardStep} />}
        {cardStep === 2 && (
          <UserGroupsStep2Configure
            setCardStep={setCardStep}
            userGroups={userGroups}
            setUserGroups={setUserGroups}
            addUserGroup={addUserGroup}
            removeUserGroup={removeUserGroup}
            updateUserGroup={updateUserGroup}
            addRole={addRole}
            removeRole={removeRole}
            updateRole={updateRole}
            checkDuplicateEmails={checkDuplicateEmails}
            undo={undo}
            redo={redo}
            canUndo={history.length > 0}
            canRedo={future.length > 0}
          />
        )}
        {cardStep === 3 && (
          <UserGroupsStep3Confirm
            userGroups={userGroups}
            onContinue={onContinue}
            company={company}
            collectionBrand={collectionBrand}
            userEmail={userEmail}
            setCardStep={setCardStep}
          />
        )}
      </div>
    </div>
  );
};

UserGroupsWizard.propTypes = {
  onContinue: PropTypes.func.isRequired,
  userEmail: PropTypes.string.isRequired,
  company: PropTypes.string.isRequired,
  collectionBrand: PropTypes.string.isRequired,
};

export default UserGroupsWizard;
